import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Icon } from "@/lib/icon";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import ContentSections from "@/components/public/ContentSections";
import { useLead } from "@/components/public/LeadDialog";
import NotFound from "./NotFound";
import { Picture } from "@/components/public/Picture";
import { Breadcrumbs } from "@/components/public/Breadcrumbs";

function SeoMeta({ path }) {
  useSeo(path);
  return null;
}

export default function SeoLandingPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openLead } = useLead();
  const [status, setStatus] = useState("loading");
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    // Strip legacy trailing slashes (e.g. old WordPress URLs) so /foo/ resolves to /foo.
    if (pathname !== "/" && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, ""), { replace: true });
      return;
    }
    setStatus("loading");
    api.get("/public/landing", { params: { path: pathname } })
      .then((r) => { setPage(r.data); setStatus("found"); })
      .catch(() => {
        // Honour an admin-managed redirect before falling back to the 404 page.
        api.get("/public/redirect", { params: { path: pathname } })
          .then((r) => window.location.replace(r.data.to_path || "/"))
          .catch(() => setStatus("notfound"));
      });
    api.get("/public/homepage").then((r) => setSettings(r.data.settings)).catch(() => {});
    api.get("/public/locations").then((r) => setLocations(r.data || [])).catch(() => {});
  }, [pathname]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (status === "notfound") return <NotFound />;

  const hero = page.hero || {};
  const facilities = page.facilities || [];
  const contact = page.contact || {};

  return (
    <div className="bg-white min-h-screen" data-testid="seo-landing-page">
      <SeoMeta path={pathname} />
      <Header phone={settings.phone} />

      <div className="container-cz pt-5">
        <Breadcrumbs items={[{ label: hero.title || "Page" }]} />
      </div>

      <div className="bg-gradient-to-b from-[#eef3ff] to-white border-b border-slate-100">
        <div className={`container-cz py-14 sm:py-16 ${hero.image ? "grid lg:grid-cols-2 gap-10 items-center" : "max-w-3xl"}`}>
          <div>
            {hero.eyebrow && <p className="eyebrow mb-3">{hero.eyebrow}</p>}
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight" data-testid="landing-title">{hero.title}</h1>
            {hero.subtitle && <p className="text-lg text-slate-600 mt-5">{hero.subtitle}</p>}
            <div className="flex flex-wrap gap-3 mt-7">
              <Button data-testid="landing-hero-tour" onClick={() => openLead("tour", { space_type: hero.title })} className="rounded-full h-12 px-7 gap-2">Book a Tour <ArrowRight className="w-4 h-4" /></Button>
              <Button data-testid="landing-hero-quote" onClick={() => openLead("quote", { space_type: hero.title })} variant="outline" className="rounded-full h-12 px-7 border-slate-300">Get a Quote</Button>
            </div>
          </div>
          {hero.image && (
            <div className="rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
              <Picture src={hero.image} alt={hero.image_alt || hero.title || ""} width={1000} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>

      {facilities.length > 0 && (
        <div className="container-cz py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="landing-facilities">
          {facilities.map((a, i) => (
            <div key={i} className="card-cz p-6">
              <span className="w-11 h-11 rounded-xl bg-sky-50 text-primary flex items-center justify-center mb-4"><Icon name={a.icon} className="w-5 h-5" /></span>
              <h3 className="font-display text-lg font-bold text-slate-900">{a.title}</h3>
              {a.desc && <p className="text-sm text-slate-500 mt-2 leading-relaxed">{a.desc}</p>}
            </div>
          ))}
        </div>
      )}

      <ContentSections sections={page.sections} />

      <FaqSection faqs={page.faqs} />

      {contact.enabled !== false && (
        <div className="bg-slate-50 border-t border-slate-100">
          <div className="container-cz py-16">
            <div className="card-cz p-8 sm:p-10 flex flex-col items-start gap-4 max-w-3xl" data-testid="landing-contact">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{contact.title || "Ready to get started?"}</h2>
              <p className="text-slate-600">{contact.subtitle || "Book a guided tour or request a customised quote in under a minute."}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                <Button data-testid="landing-contact-tour" onClick={() => openLead("tour", { space_type: hero.title })} className="rounded-full h-12 px-7">Book a Tour</Button>
                <Button data-testid="landing-contact-quote" onClick={() => openLead("quote", { space_type: hero.title })} variant="outline" className="rounded-full h-12 px-7 border-slate-300">Get a Quote</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {locations.length > 0 && (
        <div className="border-t border-slate-100 bg-white">
          <div className="container-cz py-14 sm:py-16" data-testid="landing-locations-strip">
            <p className="eyebrow mb-3">Our Noida Hubs</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Explore CoworkZen locations near you</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((l) => (
                <Link to={`/locations/${l.slug}`} key={l.slug} className="card-cz overflow-hidden group block" data-testid={`landing-loc-${l.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <Picture src={l.image} alt={l.image_alt || `${l.name}, ${l.sector}, Noida`} width={700} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-bold text-slate-900">{l.name}</h3>
                    {l.sector && <p className="text-sm text-slate-500 mt-1">{l.sector}</p>}
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-3">View this hub <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer settings={settings} />
    </div>
  );
}

function FaqSection({ faqs }) {
  const [open, setOpen] = useState(0);
  const list = (faqs || []).filter((f) => f.q && f.a);
  if (!list.length) return null;
  return (
    <div className="bg-white border-t border-slate-100" data-testid="landing-faq">
      <div className="container-cz py-14 sm:py-16 max-w-3xl">
        <p className="eyebrow mb-3">FAQs</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Frequently asked questions</h2>
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {list.map((f, i) => (
            <div key={i} data-testid={`landing-faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-4 text-left">
                <span className="font-display text-base sm:text-lg font-semibold text-slate-900">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="pb-5 -mt-1">
                  <p className="text-slate-600 leading-relaxed">{f.a}</p>
                  {f.link_url && (
                    <Link to={f.link_url} className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-2" data-testid={`landing-faq-link-out-${i}`}>
                      {f.link_label ? `Learn more: ${f.link_label}` : "Learn more"} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
